import type { CustomEmojiListing } from "@beacon/core/custom-emoji"
import { PlusIcon } from "lucide-react"
import { useState, type KeyboardEvent } from "react"
import { useViewport } from "@/design-system/index.ts"
import { Button } from "@/primitives/button.tsx"
import { cn } from "@/primitives/utils.ts"
import { useCustomEmoji } from "./custom.ts"

/** The key that moves the focus through the grid, by how many emoji, for a grid `columns` wide. */
const stepOf = (key: string, columns: number) =>
  ({ ArrowRight: 1, ArrowLeft: -1, ArrowDown: columns, ArrowUp: -columns })[key]

/** Moves the focus through a grid of emoji buttons with the arrow keys. */
function moveThroughGrid(event: KeyboardEvent<HTMLUListElement>) {
  const grid = event.currentTarget
  const columns = getComputedStyle(grid).gridTemplateColumns.split(" ").length || 1
  const step = stepOf(event.key, columns)
  if (step === undefined) return
  const buttons = [...grid.querySelectorAll<HTMLButtonElement>("button")]
  const at = buttons.indexOf(document.activeElement as HTMLButtonElement)
  const next = buttons[at + step]
  if (at === -1 || !next) return
  event.preventDefault()
  next.focus()
}

/*
 * The company's custom emoji, as the emoji picker's Custom tab offers them: by name, found by what a
 * name starts with or holds, Enter in the search picking the first found. The arrow keys move
 * through the grid; the emoji under the pointer or the focus is named at its foot, beside Add emoji,
 * which any Member uses to add one.
 */
export function CustomEmojiPicker({
  onPick,
  onAdd,
}: {
  onPick: (emoji: CustomEmojiListing) => void
  onAdd: () => void
}) {
  const { touch } = useViewport()
  const customs = useCustomEmoji()
  const [query, setQuery] = useState("")
  const [active, setActive] = useState<CustomEmojiListing>()
  const sought = query.trim()
  const shown = !customs ? [] : sought ? customs.search(sought, 500) : customs.offered

  return (
    <div className="flex h-80 w-full flex-col">
      <input
        type="search"
        aria-label="Find a custom emoji"
        placeholder="Find a custom emoji"
        value={query}
        // On touch the keyboard waits for a tap, so it doesn't rise over the emoji as they open.
        autoFocus={!touch}
        onChange={(event) => setQuery(event.target.value)}
        onKeyDown={(event) => {
          if (event.key !== "Enter" || !shown[0]) return
          event.preventDefault()
          onPick(shown[0])
        }}
        className="mx-2 mt-2 h-8 rounded-md bg-surface-sunken px-2.5 text-ui ring-1 ring-border outline-none ring-inset placeholder:text-subtle-foreground focus-visible:ring-ring/40 touch:h-10 touch:text-field"
      />
      <div className="relative mt-1 min-h-0 flex-1 overflow-y-auto">
        {!customs ? (
          <p className="absolute inset-0 flex items-center justify-center text-ui text-subtle-foreground">
            Loading emoji…
          </p>
        ) : shown.length === 0 ? (
          <p className="absolute inset-0 flex items-center justify-center px-6 text-center text-ui text-subtle-foreground">
            {sought ? "No custom emoji match" : "No custom emoji yet. Add the first."}
          </p>
        ) : (
          <ul
            aria-label="Custom emoji"
            onKeyDown={moveThroughGrid}
            className="grid grid-cols-8 px-1.5 pt-1.5 pb-1.5 compact:grid-cols-7 compact:justify-items-center"
          >
            {shown.map((emoji) => (
              <li key={emoji.id} className="flex">
                <button
                  type="button"
                  aria-label={`:${emoji.name}:`}
                  onClick={() => onPick(emoji)}
                  onMouseEnter={() => setActive(emoji)}
                  onFocus={() => setActive(emoji)}
                  className={cn(
                    "flex size-8 items-center justify-center rounded-md outline-none hover:bg-popover-accent focus-visible:bg-popover-accent focus-visible:ring-2 focus-visible:ring-ring compact:size-11",
                  )}
                >
                  <img
                    src={emoji.url ?? undefined}
                    alt=""
                    loading="lazy"
                    className="size-6 object-contain compact:size-7"
                  />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
      <div className="flex h-10 items-center gap-2 border-t border-border-subtle pr-1.5 pl-3">
        {active?.url ? (
          <>
            <img src={active.url} alt="" className="size-5 object-contain" />
            <span className="min-w-0 flex-1 truncate text-ui text-muted-foreground">
              :{active.name}:
            </span>
          </>
        ) : (
          <span className="min-w-0 flex-1 truncate text-ui text-subtle-foreground">
            Pick an emoji…
          </span>
        )}
        <Button type="button" variant="ghost" size="xs" onClick={onAdd}>
          <PlusIcon data-icon="inline-start" />
          Add emoji
        </Button>
      </div>
    </div>
  )
}
